import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Lab access codes.
 *
 * An 8-digit code identifies a lab or organisation and gates entry to the app.
 * Be clear about what this is and is not:
 *
 * - It is NOT a security boundary. Anything the browser can check, a determined
 *   person can skip. The real protection is that every document lives under
 *   users/{uid} and Firestore rules allow access only to that uid — so even
 *   someone who bypasses this screen sees nothing belonging to anyone else.
 * - It IS a useful gate: it keeps the sign-in page from being a public front
 *   door, and it tags each account with the lab it belongs to.
 *
 * Codes are not enumerable: rules permit `get` on a known lab document but deny
 * `list`, so you can only read a lab you can already name.
 */

export interface Lab {
  code: string;
  name: string;
  organisation: string;
  active: boolean;
}

const STORAGE_KEY = 'blisxbench:lab';

export function isWellFormed(code: string): boolean {
  return /^\d{8}$/.test(code.trim());
}

/** Remembered so the code is entered once per device, not once per visit. */
export function getStoredLab(): Lab | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Lab;
    return parsed?.code && parsed?.name ? parsed : null;
  } catch {
    return null;
  }
}

export function storeLab(lab: Lab): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lab));
  } catch {
    // Private mode or blocked storage: the code is simply asked for again.
  }
}

export function clearStoredLab(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the gate will ask again anyway.
  }
}

export type LabLookup =
  | { ok: true; lab: Lab }
  | { ok: false; reason: 'not-found' | 'inactive' | 'offline' };

export async function lookupLab(code: string): Promise<LabLookup> {
  const trimmed = code.trim();
  try {
    const snap = await getDoc(doc(db, 'labs', trimmed));
    if (!snap.exists()) return { ok: false, reason: 'not-found' };

    const data = snap.data() as Omit<Lab, 'code'>;
    if (data.active === false) return { ok: false, reason: 'inactive' };

    return {
      ok: true,
      lab: {
        code: trimmed,
        name: data.name ?? 'Lab',
        organisation: data.organisation ?? '',
        active: true,
      },
    };
  } catch (e) {
    // A denied read and a dead network look similar from here. Treat both as
    // "try again" rather than telling someone their valid code is wrong.
    const code = (e as { code?: string })?.code ?? '';
    if (code.includes('unavailable') || !navigator.onLine) {
      return { ok: false, reason: 'offline' };
    }
    return { ok: false, reason: 'not-found' };
  }
}
